import React from "react";
import Traec from "traec";
import DataTable from "react-data-table-component";
import { BSCard, BSBtnDropdown } from "traec-react/utils/bootstrap";
import { ErrorBoundary } from "storybook-dashboard/errors/handleError";
import { ProjectPermission } from "storybook-dashboard/permissions/project";
import { CompanyPermission } from "storybook-dashboard/permissions/company";
import { ActionButton } from "traec-react/user/button";
import { alertSuccess } from "traec-react/utils/sweetalert";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import Im from "immutable";

// Helper functions
const roleName = (invite) => invite.getIn(["project_discipline", "name"]) || invite.getIn(["auth", "name"]);

const getCompanyAndProjectId = (invite) => ({
  projectId: invite.getIn(["project_discipline", "project", "uid"]),
  companyId: invite.getIn(["company", "uid"]),
});

const getUrl = (invite) => {
  let { projectId, companyId } = getCompanyAndProjectId(invite);
  let inviteId = invite.get("uid");
  return projectId ? `/api/project/${projectId}/invite/${inviteId}/` : `/api/company/${companyId}/invite/${inviteId}/`;
};

const respondInvite = (invite, accept) => {
  let status = accept ? "accepted" : "rejected";
  let url = getUrl(invite);

  doFetch(url, "PUT", { status })
    .then(() => location.reload())
    .catch((errors) => {
      console.log("Handling request errors", errors);
      alertSuccess({
        iconType: "error",
        title: "Error",
        text: errors,
      });
    });
};

const deleteItem = (invite) => {
  let url = getUrl(invite);
  doFetch(url, "DELETE").then(() => location.reload());
};

const getColumns = ({ isRequest, projectId, companyId }) => {
  const PermissionWrapper = projectId ? ProjectPermission : CompanyPermission;
  return [
    {
      name: "Email",
      selector: (row) => row.get("email"),
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Role",
      selector: (row) => roleName(row),
      sortable: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Actions",
      cell: (row) => {
        if (isRequest) {
          return (
            <PermissionWrapper projectId={projectId} companyId={companyId} requiresAdmin={true}>
              <div className="d-flex gap-1">
                <ActionButton color="success" text="Accept" onClickHandler={(e) => respondInvite(row, true)} />
                <ActionButton color="danger" text="Reject" onClickHandler={(e) => respondInvite(row, false)} />
              </div>
            </PermissionWrapper>
          );
        } else {
          return (
            <PermissionWrapper projectId={projectId} companyId={companyId} requiresAdmin={true}>
              <BSBtnDropdown
                links={[
                  { name: "Accept on behalf of", onClick: (e) => respondInvite(row, true) },
                  { name: "Delete", onClick: (e) => deleteItem(row) },
                ]}
              />
            </PermissionWrapper>
          );
        }
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];
};

function InviteListHeader({ projectId, requestCount, inviteCount }) {
  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="h3 mb-0 text-primary">Pending User Invites</h1>
            </div>
            <p className="text-muted mb-2">
              {projectId ? "Project invitation management" : "Organisation invitation management"}
            </p>
            <div className="d-flex gap-3">
              {/* <p className="text-muted small mb-0">
                <strong>Pending requests:</strong> {requestCount}
              </p> */}
              <p className="text-muted small mb-0">
                <strong>Sent invites:</strong> {inviteCount}
              </p>
            </div>
          </div>
          <div className="text-end">
            <span className="badge badge-primary">Invite Management</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteDescription() {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">Invitation Management</h2>
      <p className="text-muted">
        Manage sent invitations to users. You can approve or reject requests, and manage outstanding invitations that
        haven't been accepted yet.
      </p>
      <div className="alert alert-info">
        <div className="d-flex">
          <span className="me-2">💡</span>
          <div>
            <strong>How it works:</strong> You can send direct invitations to users. Accepted invitations will
            automatically add users to your team.
          </div>
        </div>
      </div>
    </div>
  );
}

export function SubInviteList({ items, title, projectId, companyId }) {
  const isRequest = title === "Requests";

  return (
    <ErrorBoundary>
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="h5 mb-0">{title}</h4>
          <span className="badge bg-light text-dark">
            {items.size} {title.toLowerCase()}
          </span>
        </div>
        <div className="table-responsive">
          <DataTable
            columns={getColumns({ isRequest, projectId, companyId })}
            data={items.toArray()}
            highlightOnHover={true}
            pagination={items.size > 10}
            paginationPerPage={10}
            noDataComponent={
              <div className="text-center py-4">
                <span className="text-muted fs-1 mb-3 d-block">📧</span>
                <h6 className="text-muted">No {title.toLowerCase()} found</h6>
                <p className="text-muted small">
                  {isRequest
                    ? "No pending access requests at the moment."
                    : "No outstanding invitations. All invites have been processed."}
                </p>
              </div>
            }
            customStyles={{
              table: {
                style: {
                  borderRadius: "8px",
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#f8f9fa",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                },
              },
              rows: {
                style: {
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default function InviteList({ companyId, projectId }) {
  const url = projectId ? `/api/project/{projectId}/invite/` : `/api/company/{companyId}/invite/`;
  const { data: invites } = useApi(url, { projectId, companyId });

  console.log("InviteList", companyId, projectId, invites?.toJS());

  // Get the company requests
  let requestList = (invites || Im.List())
    .toList()
    .filter((i) => i && i.has("uid") && i.get("is_request"))
    .sortBy((obj, i) => obj.get("created"));

  // Get the list of Invites
  let inviteList = (invites || Im.List())
    .toList()
    .filter((i) => i && i.has("uid") && !i.get("is_request"))
    .sortBy((obj, i) => obj.get("created"));

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <InviteListHeader projectId={projectId} requestCount={requestList.size} inviteCount={inviteList.size} />

          <div className="card shadow-sm">
            <div className="card-body">
              <InviteDescription />

              {/* <SubInviteList items={requestList} title="Access Requests" {...{ projectId, companyId }} /> */}
              <SubInviteList items={inviteList} title="Sent Invitations" {...{ projectId, companyId }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
